/**
 * PlayerRosterPanel — player roster strip + expandable side panel.
 *
 * Two pieces:
 *   • PlayerRosterStrip — the collapsed chip strip. Renders in one of
 *     two orientations:
 *       - 'landscape' (vertical column): designed to be rendered INSIDE
 *         MapView via its `rightOverlay` prop so the strip vertically
 *         centres on the map's own height (not the screen's). During
 *         placement/attack/fortify the HUD takes ~30-40 % of the
 *         screen, so screen-centring would push the strip into the
 *         HUD area; map-centring keeps it on the play surface.
 *       - 'portrait' (horizontal row): rendered ABOVE the map in a
 *         fixed-height top bar alongside the cog. Chips lay out
 *         left-to-right; a single trailing chevron expands the panel.
 *   • PlayerRosterPanel — the slide-in panel + dim backdrop
 *     that appears when expanded. Stays at the screen-overlay level,
 *     anchored below the portrait top bar so navigation remains visible.
 *
 * The owner of these two pieces (the game screen) holds the
 * expand/collapse state and threads it through both.
 *
 * Active player gets a thicker player-color outline. Tapping any chip
 * or the chevron expands the side panel; tapping the backdrop or the
 * × button collapses it.
 */

import React, { useMemo, useState } from 'react'
import { PlayerVacationIndicator } from '../settings/PlayerVacationIndicator'
import { isVacationActive } from '../../lib/vacation'
import { CountBubble } from '../ui/CountBubble'
import { View, Text, TouchableOpacity, ScrollView } from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useRouter } from 'expo-router'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTheme } from '../../hooks/useTheme'
import { PLAYER_COLORS } from '@warcarp/ui'
import { playerColorSlot } from '../../lib/playerColor'
import { playerProfilePath } from '../../lib/botProfileLink'
import { pluralize } from '../../lib/pluralize'
import { GAME_TOP_BAR_HEIGHT } from '../../lib/gameChrome'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { formatDiceLuckRosterLabel } from '../../lib/diceLuck'
import { formatAverageTurn, visibleTurnClockLabel } from '../../lib/turnTimes'
import { useNow } from '../../hooks/useNow'
import { bootAffordance, shortBootDuration } from '../../lib/bootAffordance'
import { bootTimerAdjective } from '@warcarp/api-types'
import { eliminatedTeamBorder, teamMarkColor } from '../../lib/teamColors'
import { isTeamGame } from '@warcarp/api-types'
import { PlayerDisc } from './PlayerDisc'
import { computePlayerInfo, type PlayerInfo } from '../../lib/playerInfo'
export type { PlayerInfo } from '../../lib/playerInfo'
import { ScreenDrawer } from './ScreenDrawer'
import { HelpButton } from '../ui/HelpButton'
import { UnrankedGamesHelpModal } from '../profile/UnrankedGamesHelpModal'
import type { GameState } from '../../lib/gameState'
import type { Card, DiceLuckPlayerSummary, GamePlayerResponse, TeamId } from '@warcarp/api-types'
import { areAllies, teamChatTeam, teamLabel, teamOf, teamShape, type TeamShapeName } from '@warcarp/api-types'
import { groupByTeam, knownShareMeasures, teamShares, teamTotals, viewerTeamFirst, type TeamTotals } from '../../lib/teamPlay'
import { TeamShape } from './TeamShape'
import { TeamShareBar } from './TeamShareBar'
import { teamChatRoute } from '../../lib/teamChat'
import { cardImagery } from '../../lib/cards'

/**
 * AvatarChip — player initial on the slot's player-color, wrapped in a ring
 * of the same slot color. The chip background is the slot color (the color
 * seen on the board) to keep the player→side association unambiguous on the
 * play surface, where the board color is the identity signal.
 *
 * When `isLive` is true, a soft green glow halos the outer ring so
 * observers can see which players are currently connected.
 */
function AvatarChip({
  player,
  slotColor,
  active,
  isLive,
  size = 28,
  shape,
}: {
  player: GamePlayerResponse
  slotColor: string
  active: boolean
  isLive: boolean
  size?: number
  /** Team games: the chip is drawn in the player's team shape. */
  shape?: TeamShapeName
}) {
  // Active player gets a 3px slot-colour ring AND an amber halo so it
  // reads as "on turn" at a glance even at chip-strip size. The amber
  // halo is GATED on isLive — an absent on-turn player gets the ring
  // but NOT the halo so observers can tell at a glance that the game
  // is waiting on someone who isn't actually here. Live-but-not-on-
  // turn players get the green halo.
  //
  // The glow is a radial gradient in the same SVG as the ring (PlayerDisc).
  // There is no native shadow: Android's elevation shadow is lit from above
  // and pushed the glow below the chip.
  // Theme-aware halo + accent colours. The bright tailwind amber-400
  // / green-500 read fine against dark canvas but disappear into the
  // cream paper bg of light mode. Use the deeper amber-700 / green-700
  // shades in light, which keep their identity against the cream and
  // still pop against the slot colour ring.
  const { theme } = useTheme()
  const amber = theme === 'dark' ? '#fbbf24' : '#b45309'
  const green = theme === 'dark' ? '#22c55e' : '#15803d'
  const showAmber = active && isLive
  const showGreen = !active && isLive
  const haloColor = showAmber ? amber : showGreen ? green : null
  const ringWidth = active ? 3 : 2
  const innerRingSize = size + ringWidth * 2 + 2
  const containerSize = avatarChipBox(size, active)
  return (
    // The container keeps its size (room for the widest glow) so roster rows
    // lay out exactly as before. The disc, ring, glow and on-turn chevron are
    // one concentric SVG (PlayerDisc), so they stay aligned on every platform.
    <View style={{ width: containerSize, height: containerSize, alignItems: 'center', justifyContent: 'center' }}>
      <PlayerDisc
        initial={player.displayName?.charAt(0).toUpperCase() ?? '?'}
        fill={slotColor}
        diameter={size}
        ringWidth={ringWidth}
        ringGap={1}
        ringColor={active ? amber : slotColor}
        glowColor={haloColor}
        glowBleed={(containerSize - innerRingSize) / 2}
        // Small downward-pointing chevron above the chip, in amber, so the
        // current turn reads even without colour vision.
        marker={active ? { color: amber, width: 10, height: 6, gap: 4 } : null}
        shape={shape}
      />
      {active && <View accessibilityLabel="On turn" style={{ position: 'absolute', width: 0, height: 0 }} />}
    </View>
  )
}

/** The avatar chip's box: disc, ring and room for the widest halo. */
function avatarChipBox(size: number, active: boolean): number {
  const ringWidth = active ? 3 : 2
  return size + ringWidth * 2 + 2 + 14 + 2
}

/** Drawer row avatars, and the row's left padding (px-2) before them. */
const ROW_AVATAR_SIZE = 28
const ROW_PADDING_X = 8
/** Every team header is as tall as the one carrying the team chat bubble. */
const TEAM_HEADER_MIN_HEIGHT = 44

const STRIP_WIDTH = 44

/** A team's header in the player drawer: the team, its combined board and who is left. */
function TeamHeader({ team, totals, size, isYours, chat, inkColor }: {
  team: TeamId
  totals: TeamTotals
  size: number
  isYours: boolean
  /** The viewer's own team: the secret team chat bubble and its unread count. */
  chat?: { unread: number; onPress: () => void }
  inkColor: string
}) {
  const { theme } = useTheme()
  // The mockup's header marks: blood for your team, olive for the others.
  const markColor = teamMarkColor(theme, isYours)
  const figures = totals.partial
    ? `${pluralize(totals.troops, 'troop')} on ${pluralize(totals.territories, 'territory', 'territories')} in view`
    : `${pluralize(totals.troops, 'troop')}, ${pluralize(totals.territories, 'territory', 'territories')}`
  // Every member out: the team itself is out, marked like an eliminated row
  // (struck name, dimmed shape, italic "eliminated"), with no totals.
  const out = totals.alive === 0
  return (
    <View className="flex-row items-center gap-2 pr-1" style={{ minHeight: TEAM_HEADER_MIN_HEIGHT }}
      testID={out ? `roster-team-${team}-eliminated` : undefined}>
      {/* The mark sits in a column as wide as a row's avatar box, so it is
          centred over the player shapes below and the team name lines up
          with the player names. */}
      <View style={{ width: avatarChipBox(ROW_AVATAR_SIZE, false), marginLeft: ROW_PADDING_X, alignItems: 'center', ...(out ? { opacity: 0.35 } : null) }}
        testID={`roster-team-${team}-mark`}>
        <TeamShape team={team} size={18} fill={markColor} />
      </View>
      <View className="flex-1 flex-row items-baseline justify-between gap-2" accessibilityRole="header"
        accessibilityLabel={out
          ? `${teamLabel(team)}${isYours ? ', your team' : ''}: eliminated`
          : `${teamLabel(team)}${isYours ? ', your team' : ''}: ${figures}${totals.alive < size ? `, ${totals.alive} of ${size} still in` : ''}`}>
        <View className="flex-row items-baseline gap-2">
          <Text className="text-ink font-stencil text-xs uppercase tracking-widest" testID={`roster-team-${team}-name`}
            style={out ? { textDecorationLine: 'line-through', opacity: 0.6 } : undefined}>
            {teamLabel(team)}{isYours ? <Text className="text-blood"> · yours</Text> : null}
          </Text>
          {out && <Text className="text-ink-soft text-[10px] font-typed italic">eliminated</Text>}
        </View>
        {!out && (
          <Text className="text-ink-soft font-typed text-xs flex-shrink" numberOfLines={1} testID={`roster-team-${team}-totals`}>
            {figures}{totals.alive < size ? ` · ${totals.alive}/${size} in` : ''}
          </Text>
        )}
      </View>
      {chat && (
        // One secret group chat for the whole team; team games have no 1:1
        // bubbles on the rows. The red bubble counts unread team messages.
        <TouchableOpacity
          onPress={chat.onPress}
          testID="roster-team-chat"
          accessibilityRole="button"
          accessibilityLabel={
            chat.unread > 0
              ? `${teamLabel(team)} secret chat, ${chat.unread} unread message${chat.unread === 1 ? '' : 's'}`
              : `Open the ${teamLabel(team)} secret chat`
          }
          className="bg-paper border border-olive rounded-full items-center justify-center"
          style={{ width: 40, height: 40, marginBottom: 4, position: 'relative' }}
        >
          <MaterialCommunityIcons name="forum-outline" size={20} color={inkColor} />
          {chat.unread > 0 && (
            <View pointerEvents="none" style={{ position: 'absolute', top: -5, right: -5 }} testID="roster-team-chat-unread">
              <CountBubble count={chat.unread} />
            </View>
          )}
        </TouchableOpacity>
      )}
    </View>
  )
}

/** A teammate's hand, read-only (decision 10). Only the owner can turn cards in. */
function TeammateCards({ cards, territories, playerId }: { cards: readonly Card[]; territories: ReadonlyArray<{ id: string; name: string }>; playerId: string }) {
  if (cards.length === 0) {
    return <Text className="text-ink-soft text-xs font-typed italic" testID={`roster-teammate-cards-${playerId}`}>No cards</Text>
  }
  const label = cards.map((card) => {
    const imagery = cardImagery(card.type)
    const territory = card.territoryId ? territories.find((t) => t.id === card.territoryId)?.name : undefined
    return `${imagery.emoji} ${territory ?? imagery.label}`
  }).join(' · ')
  return (
    <Text className="text-ink text-xs font-typed" testID={`roster-teammate-cards-${playerId}`}>
      <Text className="text-ink-soft">Cards: </Text>{label}
    </Text>
  )
}


/** Memoised {@link computePlayerInfo} for the roster strip, drawer and dots. */
export function usePlayerInfo(state: GameState, players: GamePlayerResponse[]): PlayerInfo[] {
  return useMemo(() => computePlayerInfo(state, players), [state, players])
}

interface StripProps {
  state: GameState
  players: GamePlayerResponse[]
  /** 0-indexed player whose turn it is. */
  currentPlayerIndex: number
  /** Called when any chip or the chevron is tapped. */
  onExpand: () => void
  /** When the expanded panel is open we disable strip taps so a
   * lingering tap doesn't immediately re-open after collapse. */
  expanded: boolean
  /**
   * Layout direction. 'landscape' draws a vertical column (intended to
   * live inside MapView's rightOverlay slot); 'portrait' draws a
   * horizontal row (intended to live in a top header bar above the
   * map alongside the cog). Defaults to 'landscape' so existing
   * callers keep the pre-portrait behaviour.
   */
  orientation?: 'landscape' | 'portrait'
  /** Set of userIds currently live (at least one open WS). Bots are
   * always live regardless of this set. */
  presence?: Set<string>
  /** Total unread secret messages across all threads — drives a badge on the
   * strip so unread DMs are visible even while the roster panel is closed. */
  unreadTotal?: number
}

/**
 * Small blood-colored count badge shown on the roster strip when the viewer
 * has unread secret messages. Absolutely positioned so it never shifts the
 * chip layout.
 */
function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null
  return (
    <View style={{ position: 'absolute', top: -7, right: -7, zIndex: 5 }}>
      <CountBubble
        count={count}
        size={18}
        ring="canvas"
        accessibilityLabel={`${count} unread secret message${count === 1 ? '' : 's'}`}
      />
    </View>
  )
}

/**
 * PlayerRosterStrip — collapsed player chip strip.
 *
 * Landscape: vertical column. Render inside MapView via the
 * `rightOverlay` prop so it centres on the map, not the screen.
 * Portrait: horizontal row. Render in the top header bar above the
 * map; chips fit alongside the cog so the play surface stays clean.
 */
export function PlayerRosterStrip({
  state,
  players,
  currentPlayerIndex,
  onExpand,
  expanded,
  orientation = 'landscape',
  presence,
  unreadTotal = 0,
}: StripProps) {
  const playerInfo = usePlayerInfo(state, players)

  // Measured width of the flex-1 container that wraps this strip in portrait
  // mode. Updated via onLayout on the outer wrapper View. Zero until the first
  // layout pass; the count-heuristic is used as a fallback until then.
  const [containerWidth, setContainerWidth] = useState(0)

  if (orientation === 'portrait') {
    const n = playerInfo.length
    // Gap tokens: gap-0.5 = 2px, gap-1 = 4px, gap-1.5 = 6px. Tighten the
    // inter-chip gap as the count climbs so 6 chips fit the constrained
    // landscape action-header strip without spilling left onto the cog.
    const gapPx = n >= 6 ? 2 : n >= 5 ? 4 : 6
    const chipGap = n >= 6 ? 'gap-0.5' : n >= 5 ? 'gap-1' : 'gap-1.5'
    // Strip internal horizontal padding: px-2 = 8px per side, plus border-2
    // (2px per side) = 20px total horizontal inset.
    const stripPaddingX = 20

    // chipSize: fit `n` chips in `containerWidth` after accounting for
    // strip padding, inter-chip gaps, and the ~20px halo overhead per chip
    // (containerSize = size + ringWidth*2+2 + 14+2 ≈ size + 22 worst-case).
    // Clamped between MIN=14 and MAX=28. Falls back to count heuristic until
    // the first onLayout fires (containerWidth === 0).
    const MIN_CHIP = 12
    const MAX_CHIP = 28
    // 24 = worst-case rendered chip width minus chipSize. AvatarChip's
    // containerSize is `size + ringWidth*2 + 2 + 16`; the ACTIVE (on-turn)
    // chip uses ringWidth 3 → size + 24, the rest use ringWidth 2 → size + 22.
    // Reserve the active width (24) for every chip so the one active chip's
    // extra 2px can't push the box past the container (which previously
    // clipped the box's left border at 6 players).
    const CHIP_OVERHEAD = 24
    const chipSize =
      containerWidth > 0
        ? Math.max(
            MIN_CHIP,
            Math.min(
              MAX_CHIP,
              Math.floor((containerWidth - stripPaddingX - gapPx * (n - 1)) / n) - CHIP_OVERHEAD,
            ),
          )
        : n >= 6 ? 18 : n === 5 ? 20 : n === 4 ? 22 : 24

    // The outer View fills the entire flex-1 space granted by the parent
    // (landscape header or portrait top bar). onLayout gives us the actual
    // rendered width, which we use to size chips (CHIP_OVERHEAD above) so the
    // bordered box always fits WITHIN this space — right-aligned via
    // alignItems:'flex-end'. We intentionally do NOT clip here: an earlier
    // overflow:hidden guard sliced the box's own left border at 5–6 players.
    // The sizing math keeps the box inside the box, so the cog stays clear
    // and the border renders in full.
    return (
      <View
        style={{ flex: 1, alignItems: 'flex-end' }}
        onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      >
        <View
          accessibilityLabel="Player roster strip"
          className={`bg-canvas/85 rounded-xl px-2 py-1.5 flex-row items-center ${chipGap} border-2 border-olive`}
          style={{ position: 'relative' }}
          pointerEvents={expanded ? 'none' : 'auto'}
        >
          <UnreadBadge count={unreadTotal} />
          {/* No chevron here — tapping any chip already expands the roster,
              so the arrow was redundant; dropping it frees horizontal space
              for the chips (matters at 5–6 players). */}
          {playerInfo.map((info) => {
            const isTurn = info.isTurn
            const slotColor = PLAYER_COLORS[playerColorSlot(info.player)].hex
            const isLive = info.player.isBot || (presence?.has(info.player.userId) ?? false)
            return (
              <TouchableOpacity
                key={info.player.id}
                onPress={onExpand}
                accessibilityLabel={`${info.player.displayName}${isTurn ? ' (on turn)' : ''}`}
                accessibilityRole="button"
                style={{ opacity: info.eliminated ? 0.35 : 1 }}
              >
                <AvatarChip
                  player={info.player}
                  slotColor={slotColor}
                  active={isTurn}
                  isLive={isLive}
                  size={chipSize}
                />
              </TouchableOpacity>
            )
          })}
        </View>
      </View>
    )
  }
  return (
    <View
      accessibilityLabel="Player roster strip"
      testID="roster-strip-landscape"
      pointerEvents="box-none"
      style={{ width: STRIP_WIDTH }}
    >
      <View
        style={{
          width: STRIP_WIDTH,
          alignItems: 'center',
          position: 'relative',
        }}
        className="bg-canvas/85 rounded-l-xl py-2 gap-1.5 border-l-2 border-t-2 border-b-2 border-olive"
        pointerEvents={expanded ? 'none' : 'auto'}
      >
        <UnreadBadge count={unreadTotal} />
        {playerInfo.map((info) => {
          const isTurn = info.isTurn
          const slotColor = PLAYER_COLORS[playerColorSlot(info.player)].hex
          const isLive = info.player.isBot || (presence?.has(info.player.userId) ?? false)
          return (
            <TouchableOpacity
              key={info.player.id}
              onPress={onExpand}
              accessibilityLabel={`${info.player.displayName}${isTurn ? ' (on turn)' : ''}`}
              accessibilityRole="button"
              style={{ opacity: info.eliminated ? 0.35 : 1 }}
            >
              <AvatarChip player={info.player} slotColor={slotColor} active={isTurn} isLive={isLive} />
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

interface PanelProps {
  state: GameState
  players: GamePlayerResponse[]
  /** 0-indexed player whose turn it is. */
  currentPlayerIndex: number
  /** Viewer's user id, for the "(you)" tag. */
  viewerUserId?: string
  /** Whether the expanded panel is visible. */
  expanded: boolean
  /** Called when the backdrop or × button is tapped. */
  onCollapse: () => void
  /** Set of userIds currently live (at least one open WS). Bots are
   * always live regardless of this set. */
  presence?: Set<string>
  /** Called when the user taps a region-bonus entry. The panel should
   * collapse and the caller navigates to the map-details view focused
   * on that region. */
  onShowRegion?: (regionId: string) => void
  /** Measured parent-local bottom Y of the portrait top bar. When provided, the
   * tray anchors to it exactly (robust on native); falls back to an
   * inset+height estimate until measured. Ignored in landscape. */
  navBarBottom?: number | null
  /** Game id — used to route to a player's secret-message thread. When absent
   * the "Message" action is hidden (e.g. lobby, where DMs aren't offered). */
  gameId?: string
  /** userId → unread direct-message count, for per-row badges. */
  unreadByUser?: Record<string, number>
  /** Team games: unread messages in the viewer's secret team chat (the team
   * header's bubble). Team games show no 1:1 bubbles on the rows. */
  teamChatUnread?: number
  /** Current complete-log result, keyed to engine slot ids. */
  diceLuckRows?: DiceLuckPlayerSummary[] | null
  diceLuckLoading?: boolean
  /** Opens the shared Dice Luck modal focused on this engine slot id. */
  onShowDiceLuck?: (playerId: string) => void
  /** When the current turn began; drives the "On the clock" line. ACTIVE games only. */
  turnStartedAt?: string | null
  /** false for a bots-only (unranked) game — shown at the top of the drawer. */
  isRanked?: boolean
  bootableAt?: string | null
  bootAfterSeconds?: number
  onBootPlayer?: (userId: string, name: string) => void
}

/**
 * PlayerRosterPanel — screen-level expanded panel + dim backdrop. Only
 * renders when `expanded` is true. Render this at the screen overlay
 * level (NOT inside MapView) because it intentionally covers the whole
 * viewport behind a dim backdrop.
 */
export function PlayerRosterPanel({
  state,
  players,
  currentPlayerIndex,
  viewerUserId,
  expanded,
  onCollapse,
  presence,
  onShowRegion,
  navBarBottom,
  gameId,
  unreadByUser,
  teamChatUnread = 0,
  diceLuckRows,
  diceLuckLoading = false,
  onShowDiceLuck,
  turnStartedAt,
  isRanked,
  bootableAt,
  bootAfterSeconds = 432000,
  onBootPlayer,
}: PanelProps) {
  const [unrankedHelpOpen, setUnrankedHelpOpen] = useState(false)
  // Ticks every second while the drawer is open so the turn clock counts up.
  const now = useNow(expanded && !!turnStartedAt ? 1_000 : null)
  const playerInfo = usePlayerInfo(state, players)
  // Team games: the viewer's seat and team, for the "your team" header tag and
  // teammates' hands (decision 10: teammates see each other's cards).
  const viewerSeat = players.find((p) => p.userId === viewerUserId && !p.isBot)
  const viewerPlayerId = viewerSeat ? String(viewerSeat.slot) : null
  const viewerTeam = teamOf(state, viewerPlayerId)
  // Team games: the viewer's secret team chat, when they can use it (seated,
  // not resigned). It replaces every 1:1 secret message bubble.
  const teamGame = isTeamGame({ state })
  const viewerChatTeam = gameId ? teamChatTeam(state, players, viewerUserId) : null
  const diceLuckByPlayer = useMemo(
    () => new Map((diceLuckRows ?? []).map((row) => [row.playerId, row])),
    [diceLuckRows],
  )
  const insets = useSafeAreaInsets()
  const router = useRouter()
  const { theme } = useTheme()
  // Ink tint for the monochrome message icon (mirrors GameTopBar's INK).
  const inkColor = theme === 'dark' ? '#e6e1c4' : '#18170f'
  // In landscape the tray stops at the map container's left edge (covers only
  // the map area); the backdrop dims the action-column side. Portrait = full
  // width.
  const { isLandscape, actionPanelWidth } = useMapDimensions(state)
  const trayLeft = isLandscape ? actionPanelWidth : 0
  // Landscape has no nav bar on the RIGHT (map) side, so the tray uses the full
  // height there; portrait sits below the full-width nav bar. Prefer the
  // measured on-screen nav-bar bottom (robust on native); fall back to the
  // inset+height estimate until it's measured.
  const trayTop = isLandscape
    ? insets.top
    : navBarBottom ?? insets.top + GAME_TOP_BAR_HEIGHT

  return (
    <>
    <ScreenDrawer
      visible={expanded}
      onClose={onCollapse}
      top={trayTop}
      left={trayLeft}
      slideFrom="right"
      // Slide back out to the right on web too (native always animates).
      animateExitOnWeb
      accessibilityLabel="Player list"
      backdropLabel="Close player list"
    >
      {(dismissThenNavigate) => (
      <View
        // Olive rule on the top (like the cards sheet), left and bottom.
        className="flex-1 bg-paper border-t-2 border-l-2 border-b-2 border-olive px-3 pt-1 pb-3 gap-1"
      >
        {/* Header row: the close X on the right. Bots-only games don't count
            toward rankings, so they say so on the left, with a "?" that
            explains why; ranked games leave that space empty. */}
        <View className="flex-row items-center justify-between">
          {isRanked === false ? (
            <View className="flex-row items-center" style={{ gap: 8 }} testID="player-roster-unranked">
              <Text className="font-stencil text-blood text-xs uppercase tracking-widest">Unranked</Text>
              <HelpButton
                size={22}
                accessibilityLabel="Why is this game unranked?"
                testID="player-roster-unranked-help"
                onPress={() => setUnrankedHelpOpen(true)}
              />
            </View>
          ) : <View />}
          <TouchableOpacity
            onPress={onCollapse}
            accessibilityLabel="Close players"
            accessibilityRole="button"
            testID="player-roster-close"
            // Compact row; hitSlop keeps the 44 pt touch target.
            hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
            style={{ width: 32, height: 32, marginRight: -6, alignItems: 'center', justifyContent: 'center' }}
          >
            <MaterialCommunityIcons name="close" size={24} color={inkColor} />
          </TouchableOpacity>
        </View>
        <ScrollView contentContainerStyle={{ gap: 6, paddingBottom: 24 }}>
          {(() => {
          const renderRow = (info: PlayerInfo) => {
            const isTurn = info.isTurn
            const isYou = info.player.userId === viewerUserId
            const slotColor = PLAYER_COLORS[playerColorSlot(info.player)].hex
            // Bots never read as live/online (only live humans get the halo),
            // matching the top-bar dot strip.
            const isLive = !info.player.isBot && (presence?.has(info.player.userId) ?? false)
            // Stable bots link to Synthetic Command; humans keep public profiles.
            const profileHref = playerProfilePath({
              isBot: info.player.isBot,
              botProfile: info.player.botProfile,
              username: info.player.user?.username,
            })
            const canViewProfile = profileHref !== null
            const goToProfile = () => {
              if (!profileHref) return
              dismissThenNavigate(() => router.push(profileHref))
            }
            const profileA11y = `View ${info.player.displayName}'s profile`
            // Secret-message affordance: any real (non-bot) opponent, once we
            // have a gameId to route to. Bots can't receive DMs.
            const away = isVacationActive(info.player.vacation, now)
            const playerId = String(info.player.slot)
            // Team games: every avatar is drawn in its team's shape.
            const rowTeam = teamOf(state, playerId)
            const shape = rowTeam ? teamShape(rowTeam) : undefined
            // Team games have no 1:1 secret messages: the team chat on the
            // viewer's team header is the only secret channel.
            const canMessage = !!gameId && !isYou && !info.player.isBot && !away && !teamGame
            const unread = unreadByUser?.[info.player.userId] ?? 0
            const playerDiceLuck = diceLuckByPlayer.get(playerId)
            const turnClock = isTurn ? visibleTurnClockLabel(turnStartedAt, info.player.vacation, now) : null
            const bootState = bootAffordance({ bootableAt: isTurn && !info.eliminated && !away ? bootableAt : null, bootAfterSeconds, now, viewerCanBoot: !!onBootPlayer && !isYou && !info.player.isBot && players.some(p => p.userId === viewerUserId && !p.isBot) })
            const bootTimerLabel = bootTimerAdjective(bootAfterSeconds)
            const diceLuckLabel = formatDiceLuckRosterLabel(playerDiceLuck, diceLuckLoading)
            const goMessage = () => {
              if (!canMessage) return
              dismissThenNavigate(() => {
                router.push(
                  `/(app)/games/${gameId}/dm/${info.player.userId}?name=${encodeURIComponent(
                    info.player.displayName,
                  )}`,
                )
              })
            }
            return (
              <View
                key={info.player.id}
                className={`px-2 py-2 rounded-md ${
                  isTurn ? 'bg-blood/20' : 'bg-paper-up'
                }`}
                style={isTurn ? { borderWidth: 2, borderColor: slotColor } : undefined}
              >
                {/* Avatar left, text column right — both top-aligned so the
                    text hugs the top of the avatar instead of centring (which
                    wasted the space beside the tall avatar/halo box). */}
                <View className="flex-row items-start gap-2">
                  {canViewProfile ? (
                    <TouchableOpacity
                      onPress={goToProfile}
                      accessibilityLabel={profileA11y}
                      accessibilityRole="button"
                      activeOpacity={0.6}
                    >
                      <AvatarChip player={info.player} slotColor={slotColor} active={isTurn} isLive={isLive} size={ROW_AVATAR_SIZE} shape={shape} />
                    </TouchableOpacity>
                  ) : (
                    <AvatarChip player={info.player} slotColor={slotColor} active={isTurn} isLive={isLive} size={ROW_AVATAR_SIZE} shape={shape} />
                  )}
                  <View className="flex-1 gap-0.5">
                    <View className="flex-row items-center gap-2">
                      {canViewProfile ? (
                        <TouchableOpacity
                          className="flex-1"
                          onPress={goToProfile}
                          accessibilityLabel={profileA11y}
                          accessibilityRole="button"
                          activeOpacity={0.6}
                        >
                          <Text
                            className="text-ink font-typed text-sm underline"
                            numberOfLines={1}
                            style={info.eliminated ? { textDecorationLine: 'line-through', opacity: 0.6 } : undefined}
                          >
                            {info.player.displayName}
                            {isYou ? ' (you)' : ''}
                            {info.player.isBot ? ' 🤖' : ''}
                          </Text>
                        </TouchableOpacity>
                      ) : (
                        <Text
                          className="text-ink font-typed text-sm flex-1"
                          numberOfLines={1}
                          style={info.eliminated ? { textDecorationLine: 'line-through', opacity: 0.6 } : undefined}
                        >
                          {info.player.displayName}
                          {isYou ? ' (you)' : ''}
                          {info.player.isBot ? ' 🤖' : ''}
                        </Text>
                      )}
                    </View>
                {!info.eliminated && (() => {
                  return (
                    <View className="gap-0.5">
                      {info.fog ? (
                        // Fog of war: only what this player's side can see.
                        <>
                          <Text className="text-ink text-xs font-typed" testID={`roster-fog-seen-${info.player.slot}`}>
                            {pluralize(info.fog.seenTroops, 'troop')} on{' '}
                            {pluralize(info.fog.seenTerritories, 'territory', 'territories')} in view
                          </Text>
                          <Text className="text-ink-soft text-xs font-typed italic">
                            {info.fog.ownershipPublic
                              ? `${pluralize(info.owned, 'territory', 'territories')} · troops and cards hidden`
                              : 'Territories, troops and cards hidden'}
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text className="text-ink-soft text-xs font-typed">
                            {pluralize(info.totalTroops, 'troop')},{' '}
                            {pluralize(info.owned, 'territory', 'territories')},{' '}
                            {pluralize(info.cardCount, 'card')}
                          </Text>
                          <Text className="text-ink-soft text-xs font-typed">
                            Next reinforce: {info.nextReinforce}
                          </Text>
                          {areAllies(state, viewerPlayerId, playerId) && (
                            <TeammateCards cards={state.cards?.[playerId] ?? []} territories={state.map.territories} playerId={playerId} />
                          )}
                        </>
                      )}
                      {info.regionBonuses.length > 0 && (
                        // Bonuses inline on one wrapped, comma-separated line to
                        // save vertical space; each is tappable to show it on
                        // the map.
                        <View className="flex-row flex-wrap items-center">
                          <Text className="text-olive-up text-xs font-typed">
                            {info.regionBonuses.length === 1 ? 'Bonus: ' : 'Bonuses: '}
                          </Text>
                          {info.regionBonuses.map((r, i) => {
                            const label = `${r.name} +${r.bonus}`
                            const isLast = i === info.regionBonuses.length - 1
                            return (
                              <React.Fragment key={r.id}>
                                {onShowRegion ? (
                                  <TouchableOpacity
                                    onPress={() => onShowRegion(r.id)}
                                    accessibilityLabel={`Show ${r.name} region on map`}
                                    accessibilityRole="button"
                                    activeOpacity={0.6}
                                  >
                                    <Text className="text-olive-up text-xs font-typed underline">
                                      {label}
                                    </Text>
                                  </TouchableOpacity>
                                ) : (
                                  <Text className="text-olive-up text-xs font-typed">{label}</Text>
                                )}
                                {/* Plain (non-underlined) separator with a
                                    trailing space so links read as a list. */}
                                {!isLast && (
                                  <Text className="text-olive-up text-xs font-typed">, </Text>
                                )}
                              </React.Fragment>
                            )
                          })}
                        </View>
                      )}
                    </View>
                  )
                })()}
                {info.eliminated && (
                  <Text className="text-ink-soft text-[10px] font-typed italic">
                    {info.resigned ? 'resigned' : 'eliminated'}
                  </Text>
                )}
                {onShowDiceLuck && (
                  <View className="flex-row flex-wrap items-baseline gap-1 py-0.5">
                    <Text className="text-ink-soft text-xs font-typed">
                      Dice luck:
                    </Text>
                    <TouchableOpacity
                      onPress={() => {
                        // A sibling modal can pause this drawer's web animation.
                        // Park it synchronously so closing Dice Luck never reveals
                        // a half-finished exit behind it.
                        dismissThenNavigate(
                          () => onShowDiceLuck(playerId),
                          { reuseHistoryEntry: true, parkImmediately: true },
                        )
                      }}
                      accessibilityRole="link"
                      accessibilityLabel={`View ${info.player.displayName}'s dice luck`}
                      activeOpacity={0.65}
                    >
                      <Text className="text-blood text-xs font-typed underline">
                        {diceLuckLabel}
                      </Text>
                    </TouchableOpacity>
                  </View>
                )}
                {/* Bots move instantly, so an average turn time says nothing. */}
                {info.player.turnCount !== undefined && !info.player.isBot && (
                  <Text className="text-ink-soft text-xs font-typed" testID={`roster-avg-turn-${playerId}`}>
                    Avg turn: {formatAverageTurn(info.player.averageTurnSeconds)}
                  </Text>
                )}

                  </View>
                  {/* Right column: the turn clock (current player only) on the
                      name line, right-aligned with the private-message button
                      below it. The button stays vertically centred in what is
                      left; the red bubble mirrors the main game-chat unread badge. */}
                  {(turnClock !== null || canMessage || away) && (
                    <View style={{ alignSelf: 'stretch', alignItems: 'flex-end', flexShrink: 0, gap: 4, width: away && info.player.vacation?.message ? '38%' : undefined, maxWidth: away && info.player.vacation?.message ? 220 : undefined }}>
                      {turnClock !== null && (
                        <Text className={`text-xs font-typed ${bootState === 'boot' ? 'text-blood' : 'text-ink-soft'}`} testID="roster-turn-clock">
                          Turn for{' '}
                          <Text className="text-ink text-sm font-typed">{turnClock}</Text>
                        </Text>
                      )}
                      {isTurn && away && <Text className="text-xs font-typed text-ink-soft" testID="roster-turn-paused">Turn timer paused</Text>}
                      {bootState === 'hint' && bootableAt && <Text className="text-ink-soft text-xs font-typed italic" testID="roster-boot-hint">Boot in {shortBootDuration(Date.parse(bootableAt) - now)}</Text>}
                      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'flex-end' }}>
                        {away ? <PlayerVacationIndicator name={info.player.displayName} vacation={info.player.vacation} icon /> : canMessage && (
                          <TouchableOpacity
                            onPress={goMessage}
                            accessibilityLabel={
                              unread > 0
                                ? `Message ${info.player.displayName}, ${unread} unread secret message${unread === 1 ? '' : 's'}`
                                : `Send ${info.player.displayName} a secret message`
                            }
                            accessibilityRole="button"
                            className="bg-paper border border-olive rounded-full items-center justify-center"
                            style={{ width: 40, height: 40, position: 'relative' }}
                          >
                            <MaterialCommunityIcons name="message-lock-outline" size={20} color={inkColor} />
                            {unread > 0 && (
                              <View pointerEvents="none" style={{ position: 'absolute', top: -3, right: -3 }}>
                                <CountBubble count={unread} />
                              </View>
                            )}
                          </TouchableOpacity>
                        )}
                        {away && info.player.vacation?.message && <Text className="text-ink font-typed text-xs text-right" style={{ marginTop: 4 }}>{info.player.vacation.message}</Text>}
                      </View>
                    </View>
                  )}
                </View>
                {bootState === 'boot' && bootableAt && <View className="mt-2 pt-2 border-t border-dashed border-blood flex-row items-center justify-between gap-2" testID="roster-boot-strip">
                  <View className="flex-1"><Text className="text-blood font-typed text-xs">⏱ Over the {bootTimerLabel} timer by {shortBootDuration(now - Date.parse(bootableAt))}.</Text><Text className="text-ink-soft font-typed text-[10px]">Hand {info.player.displayName}&apos;s seat to a bot?</Text></View>
                  <TouchableOpacity testID={`boot-player-${info.player.userId}`} accessibilityRole="button" accessibilityLabel={`Boot ${info.player.displayName}`} onPress={() => onBootPlayer?.(info.player.userId, info.player.displayName)} className="bg-blood rounded-md px-3 py-2"><Text className="font-stencil text-white text-xs">Boot {info.player.displayName}</Text></TouchableOpacity>
                </View>}
              </View>
            )
          }
          // Team games: rows grouped under team headers with team totals,
          // keeping turn order inside each team (plan §12 decision 5).
          const turnGroups = groupByTeam(state, playerInfo, (info) => String(info.player.slot))
          if (!turnGroups) return playerInfo.map(renderRow)
          // Your team first (with the team chat), then the rest in turn order.
          const groups = viewerTeamFirst(turnGroups, viewerTeam)
          // Each team's totals, computed once per render.
          const totalsByTeam = new Map(groups.map((g) => [g.team, teamTotals(g.members)]))
          // Eliminated teams drop out of the share bar: they hold nothing, and
          // their header already says they are out.
          const inPlay = groups.filter((g) => totalsByTeam.get(g.team)!.alive > 0)
          const measures = knownShareMeasures(inPlay)
          return [
            // The share bar leads the drawer: each team's slice of the board.
            measures.length > 0 ? (
              <TeamShareBar key="share" measures={measures} sharesFor={(m) => teamShares(inPlay, m)} viewerTeam={viewerTeam} />
            ) : null,
            ...groups.map((group) => {
            const totals = totalsByTeam.get(group.team)!
            const out = totals.alive === 0
            return (
            // Each team is one bordered box (header plus its rows), outlined
            // like the share bar; an eliminated team's outline dims with it.
            <View key={group.team} testID={`roster-team-${group.team}`}
              className="border rounded-lg border-olive"
              // The olive token at 35%: the class form doesn't apply to borders on web.
              style={{ gap: 6, padding: 6, ...(out ? { borderColor: eliminatedTeamBorder(theme) } : null) }}>
              <TeamHeader team={group.team} totals={totals} size={group.members.length} isYours={group.team === viewerTeam} inkColor={inkColor}
                chat={group.team === viewerChatTeam ? {
                  unread: teamChatUnread,
                  onPress: () => dismissThenNavigate(() => router.push(teamChatRoute(gameId!) as never)),
                } : undefined} />
              {group.members.map(renderRow)}
            </View>
            )
          }),
          ]
          })()}
        </ScrollView>
      </View>
      )}
    </ScreenDrawer>
    <UnrankedGamesHelpModal visible={unrankedHelpOpen} onClose={() => setUnrankedHelpOpen(false)} />
    </>
  )
}
